/**
 * SoundEngine — the ONE impure audio surface for the client.
 *
 * Owns a lazily-created AudioContext and realises {@link Cue}s from the
 * cue table via WebAudio oscillators + gain envelopes. Nothing else in
 * the app touches WebAudio directly; the engine is injected (real one in
 * production, a spy in tests) so cue-firing logic stays testable without
 * an audio backend.
 *
 * Design notes:
 *   • Autoplay policy: browsers block/park an AudioContext created before
 *     a user gesture. We create it lazily on the first `play()` AFTER the
 *     app has seen a gesture (the provider calls `unlock()` on first
 *     pointer/keydown), and `resume()` a suspended context opportunistically.
 *   • Master volume + mute persist to localStorage so the preference
 *     survives reloads.
 *   • The engine never throws into callers — audio is best-effort UX. Any
 *     WebAudio error is swallowed (logged in dev) so a missing/again-
 *     suspended context can never break gameplay.
 */

import { CUES, cueDurationSeconds, type Cue, type CueId, type Voice } from "./cues.js";

const LS_VOLUME = "pit.sound.volume";
const LS_MUTED = "pit.sound.muted";
const DEFAULT_VOLUME = 0.6;
/** Small forward offset (s) so scheduled notes never land in the past and
 *  WebAudio has a hair of lookahead for the attack ramp. */
const SCHEDULE_LOOKAHEAD_S = 0.02;

/** Minimal WebAudio surface the engine needs. Lets tests fake it.
 *  `createBufferSource`/`decodeAudioData` (#608) back the second, FILE-based
 *  playback path (`playFile`) alongside the original oscillator path. */
export interface AudioContextLike {
  readonly currentTime: number;
  readonly destination: AudioNode;
  readonly state: AudioContextState;
  createOscillator(): OscillatorNode;
  createGain(): GainNode;
  createBufferSource(): AudioBufferSourceNode;
  decodeAudioData(data: ArrayBuffer): Promise<AudioBuffer>;
  resume(): Promise<void>;
  close(): Promise<void>;
}

/** Factory so production uses `window.AudioContext` and tests inject a fake. */
export type AudioContextFactory = () => AudioContextLike | null;

/** Minimal `fetch` surface `playFile` needs to pull an asset's bytes. Lets
 *  tests inject a fake — the engine never assumes a global `fetch`. */
export type SoundFetchLike = (url: string) => Promise<{ arrayBuffer(): Promise<ArrayBuffer> }>;

/** Public engine surface consumed by React (provider/controller/hooks). */
export interface SoundEngineApi {
  /** Fire a named cue from the table. Best-effort; no-op when muted/unavailable. */
  play(id: CueId): void;
  /** Fire an ad-hoc cue (e.g. a pitched countdown tick). Best-effort. */
  playCue(cue: Cue): void;
  /**
   * #608: play a bundled audio file (an ability's committed `.mp3`, resolved
   * via `resolveAbilitySound`) instead of a synthesized cue. Decodes ONCE per
   * url and caches the decoded `AudioBuffer` — a per-hit fetch/decode would
   * be a real perf regression in fast combat (see file header). Best-effort;
   * no-op when muted/unavailable, and a fetch/decode failure is swallowed
   * (logged in dev) exactly like `playCue`'s WebAudio error handling.
   * (#795: called per IMPACT, from `impactAudio.ts` — no longer per keypress.)
   */
  playFile(url: string): void;
  /** Mark that a user gesture happened; unblocks lazy context creation. */
  unlock(): void;
  isMuted(): boolean;
  setMuted(muted: boolean): void;
  getVolume(): number;
  /** Set master volume, clamped to 0..1, persisted. */
  setVolume(v: number): void;
  /** Snapshot of internal state for diagnostics (?sounddebug). */
  debug(): SoundDebug;
  /** Release audio resources (provider unmount). */
  dispose(): void;
}

/** Diagnostic snapshot — surfaced on-screen/console when debugging silence. */
export interface SoundDebug {
  readonly unlocked: boolean;
  readonly hasContext: boolean;
  readonly contextState: string | null;
  readonly muted: boolean;
  readonly volume: number;
}

/** True when the page URL opts into sound diagnostics (?sounddebug). */
function soundDebugEnabled(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return new URLSearchParams(window.location.search).has("sounddebug");
  } catch {
    return false;
  }
}

function defaultFactory(): AudioContextLike | null {
  if (typeof window === "undefined") return null;
  const w = window as unknown as {
    AudioContext?: new () => AudioContextLike;
    webkitAudioContext?: new () => AudioContextLike;
  };
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (Ctor === undefined) return null;
  try {
    return new Ctor();
  } catch {
    return null;
  }
}

function readStoredVolume(): number {
  if (typeof localStorage === "undefined") return DEFAULT_VOLUME;
  const raw = localStorage.getItem(LS_VOLUME);
  if (raw === null) return DEFAULT_VOLUME;
  const n = Number(raw);
  return Number.isFinite(n) ? clamp01(n) : DEFAULT_VOLUME;
}

function readStoredMuted(): boolean {
  if (typeof localStorage === "undefined") return false;
  return localStorage.getItem(LS_MUTED) === "1";
}

/** Production default: the global `fetch`. Tests inject a fake via
 *  `SoundEngineOptions.fetchImpl`. */
function defaultSoundFetch(): SoundFetchLike {
  return (url: string) => fetch(url);
}

export interface SoundEngineOptions {
  readonly factory?: AudioContextFactory;
  /** Skip localStorage (tests / SSR). Defaults to reading persisted prefs. */
  readonly persist?: boolean;
  /** #608: injectable fetch for `playFile`'s asset download. Defaults to the
   *  global `fetch`. */
  readonly fetchImpl?: SoundFetchLike;
}

export class SoundEngine implements SoundEngineApi {
  private ctx: AudioContextLike | null = null;
  private readonly factory: AudioContextFactory;
  private readonly persist: boolean;
  private readonly fetchImpl: SoundFetchLike;
  private unlocked = false;
  private muted: boolean;
  private volume: number;
  /** #608: decode-once-and-cache — a per-hit fetch/decode would be a real
   *  perf regression in fast combat. Keyed by url; the cached value is the
   *  IN-FLIGHT decode promise so concurrent plays of the same not-yet-decoded
   *  url share one fetch+decode instead of racing duplicates. */
  private readonly bufferCache = new Map<string, Promise<AudioBuffer>>();

  public constructor(opts: SoundEngineOptions = {}) {
    this.factory = opts.factory ?? defaultFactory;
    this.persist = opts.persist ?? true;
    this.fetchImpl = opts.fetchImpl ?? defaultSoundFetch();
    this.muted = this.persist ? readStoredMuted() : false;
    this.volume = this.persist ? readStoredVolume() : DEFAULT_VOLUME;
  }

  public unlock(): void {
    this.unlocked = true;
    // Create AND resume inside the user gesture. Browsers only honour
    // resume() from a gesture, and doing it here (rather than on the first
    // cue) means the context is already `running` by the time any sound
    // plays — avoiding the suspended-context/past-schedule race.
    const ctx = this.ensureContext();
    if (ctx !== null && ctx.state !== "running") {
      void ctx.resume();
    }
    if (soundDebugEnabled()) {
      console.info("[sound] unlock", this.debug());
    }
  }

  public debug(): SoundDebug {
    return {
      unlocked: this.unlocked,
      hasContext: this.ctx !== null,
      contextState: this.ctx === null ? null : this.ctx.state,
      muted: this.muted,
      volume: this.volume,
    };
  }

  public isMuted(): boolean {
    return this.muted;
  }

  public setMuted(muted: boolean): void {
    this.muted = muted;
    if (this.persist && typeof localStorage !== "undefined") {
      localStorage.setItem(LS_MUTED, muted ? "1" : "0");
    }
  }

  public getVolume(): number {
    return this.volume;
  }

  public setVolume(v: number): void {
    this.volume = clamp01(v);
    if (this.persist && typeof localStorage !== "undefined") {
      localStorage.setItem(LS_VOLUME, String(this.volume));
    }
  }

  public play(id: CueId): void {
    this.playCue(CUES[id]);
  }

  public playCue(cue: Cue): void {
    if (soundDebugEnabled()) {
      console.info(`[sound] playCue ${cue.id}`, this.debug());
    }
    if (this.muted || this.volume <= 0) return;
    const ctx = this.ensureContext();
    if (ctx === null) return;
    try {
      // A freshly-created context is often `suspended` with currentTime 0.
      // resume() is async; if we scheduled at currentTime now, the notes
      // would land in the past once it resumes and never sound. So resume
      // first, then schedule from a small forward offset off the LATEST
      // currentTime (re-read after resume). The offset also gives WebAudio
      // a hair of lookahead so the attack isn't clipped.
      if (ctx.state !== "running") {
        void ctx.resume();
      }
      const base = ctx.currentTime + SCHEDULE_LOOKAHEAD_S;
      for (const voice of cue.voices) this.scheduleVoice(ctx, voice, base);
    } catch (err) {
      if (import.meta.env.DEV || soundDebugEnabled()) {
        console.warn("[sound] play failed", err);
      }
    }
  }

  public playFile(url: string): void {
    if (soundDebugEnabled()) {
      console.info(`[sound] playFile ${url}`, this.debug());
    }
    if (this.muted || this.volume <= 0) return;
    const ctx = this.ensureContext();
    if (ctx === null) return;
    this.loadBuffer(ctx, url)
      .then((buffer) => {
        // Re-check mute/volume — a decode can straddle a mute toggle.
        if (this.muted || this.volume <= 0) return;
        if (ctx.state !== "running") {
          void ctx.resume();
        }
        const src = ctx.createBufferSource();
        const gain = ctx.createGain();
        src.buffer = buffer;
        gain.gain.setValueAtTime(this.volume, ctx.currentTime);
        src.connect(gain);
        gain.connect(ctx.destination);
        src.start(ctx.currentTime + SCHEDULE_LOOKAHEAD_S);
      })
      .catch((err: unknown) => {
        if (import.meta.env.DEV || soundDebugEnabled()) {
          console.warn("[sound] playFile failed", err);
        }
      });
  }

  public dispose(): void {
    const ctx = this.ctx;
    this.ctx = null;
    this.bufferCache.clear();
    if (ctx !== null) {
      try {
        void ctx.close();
      } catch {
        // ignore
      }
    }
  }

  /** Lazily build the context once a gesture has unlocked audio. */
  private ensureContext(): AudioContextLike | null {
    if (this.ctx !== null) return this.ctx;
    if (!this.unlocked) return null;
    this.ctx = this.factory();
    return this.ctx;
  }

  /**
   * #608: fetch + decode `url` ONCE, caching the (in-flight, then resolved)
   * decode promise so every subsequent `playFile(url)` — including ones that
   * land while the first decode is still in flight — reuses it instead of
   * re-fetching/re-decoding. A failed decode evicts itself from the cache so
   * a later retry (e.g. after a transient network hiccup) can succeed.
   */
  private loadBuffer(ctx: AudioContextLike, url: string): Promise<AudioBuffer> {
    const cached = this.bufferCache.get(url);
    if (cached !== undefined) return cached;
    const promise = this.fetchImpl(url)
      .then((res) => res.arrayBuffer())
      .then((data) => ctx.decodeAudioData(data))
      .catch((err: unknown) => {
        this.bufferCache.delete(url);
        throw err;
      });
    this.bufferCache.set(url, promise);
    return promise;
  }

  /** Schedule a single oscillator + gain envelope for one voice. */
  private scheduleVoice(ctx: AudioContextLike, voice: Voice, base: number): void {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = voice.timbre;

    const t0 = base + voice.start;
    const tEnd = t0 + voice.duration;

    osc.frequency.setValueAtTime(voice.freq, t0);
    if (voice.freqTo !== undefined && voice.freqTo !== voice.freq) {
      osc.frequency.linearRampToValueAtTime(voice.freqTo, tEnd);
    }

    // Attack / hold / release envelope, scaled by master volume.
    const peak = voice.peak * this.volume;
    const attack = Math.min(voice.attack, voice.duration);
    const release = Math.min(voice.release, voice.duration - attack);
    const holdEnd = tEnd - release;
    gain.gain.setValueAtTime(0, t0);
    gain.gain.linearRampToValueAtTime(peak, t0 + attack);
    if (holdEnd > t0 + attack) {
      gain.gain.setValueAtTime(peak, holdEnd);
    }
    gain.gain.linearRampToValueAtTime(0, tEnd);

    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0);
    osc.stop(tEnd);
  }
}

/** Convenience: worst-case duration of the longest cue (for tests/tuning). */
export function longestCueSeconds(): number {
  let max = 0;
  for (const id of Object.keys(CUES) as CueId[]) {
    const d = cueDurationSeconds(CUES[id]);
    if (d > max) max = d;
  }
  return max;
}

function clamp01(x: number): number {
  if (!Number.isFinite(x)) return 0;
  if (x < 0) return 0;
  if (x > 1) return 1;
  return x;
}
